import { TEMP_REFS } from 'lib/constants';
import { now, toISO } from 'lib/utils/date';
import Routes from 'lib/utils/routes';

const { ACTIVITY_REF: optimisticActivityRef } = TEMP_REFS;

const {
  actionCanonicalActivitySetClose,
  actionCanonicalActivitySetReopen,
  actionOptimisticActivitySetClose,
  actionCloseLaborTicketModal,
  actionCanonicalActivityDelete,
  actionCanonicalActivityCreate,
  actionOptimisticActivityCreate,
  actionSetRecentOperatorRuns,
  actionOptimisticActivityUpdate,
  actionCanonicalActivityUpdate,
} = require('lib/actions');
const { throwSuccessToast, throwErrorToast } = require('lib/utils/toast');

export const stopJob = ({
  requestUpdateActivitySet,
  latestActivitySet,
  latestActivity,
}) => {
  const nowISO = toISO(now().valueOf());
  requestUpdateActivitySet({
    variables: {
      input: {
        machineRef: latestActivitySet.machineRef,
        activitySetRef: latestActivitySet.activitySetRef,
        closedAt: nowISO,
      },
    },
    optimisticResponse: {
      __typename: 'Mutation',
      updateActivitySetResponse: {
        __typename: 'ActivitySet',
        activitySet: {
          ...latestActivitySet,
          closedAt: nowISO,
          activities: [
            {
              ...latestActivity,
              // only close out activity if open
              end: latestActivity.end || nowISO,
            },
          ],
        },
      },
    },
  });
};

export const getRequestUpdateActivitySetOptions = ({
  dispatch,
  latestActivitySet,
  latestActivity,
  t,
  jobName,
}) => {
  return {
    fetchPolicy: 'no-cache',
    update: (
      _cache,
      {
        data: {
          updateActivitySetResponse: {
            activitySet: {
              activities: [activity],
              ...closedActivitySet
            },
          },
        },
      }
    ) => {
      dispatch(
        actionOptimisticActivitySetClose({
          activitySet: {
            activitySetRef: closedActivitySet.activitySetRef,
            closedAt: closedActivitySet.closedAt,
          },
          activity: {
            activityRef: activity.activityRef,
            end: activity.end,
          },
        })
      );
    },
    onCompleted: ({
      updateActivitySetResponse: {
        activitySet: {
          activities: [activity], // should be sorted start ASC
          ...closedActivitySet
        },
      },
    }) => {
      throwSuccessToast(t('Stopped:', { jobName }));
      dispatch(
        actionCanonicalActivitySetClose({
          activitySet: closedActivitySet,
          activity,
        })
      );
      dispatch(actionCloseLaborTicketModal());
    },
    onError: () => {
      dispatch(
        actionCanonicalActivitySetReopen({
          activitySet: {
            activitySetRef: latestActivitySet.activitySetRef,
            closedAt: null,
          },
          activity: {
            activityRef: latestActivity.activityRef,
            end: null,
          },
        })
      );
      throwErrorToast(t('Could not stop:', { jobName }));
    },
  };
};

export const getStopOperatorOptions = ({ history, machine, dispatch }) => {
  return {
    fetchPolicy: 'no-cache',
    onCompleted: ({ stopOperatorResponse }) => {
      if (stopOperatorResponse) {
        const {
          machine: localMachine,
          operator,
          ...operatorRun
        } = stopOperatorResponse;
        const mappedPayload = {
          operatorRuns: [
            {
              ...operatorRun,
              machineId: localMachine?.id,
              operatorId: operator?.id,
            },
          ],
        };
        dispatch(actionSetRecentOperatorRuns(mappedPayload));
      }
      dispatch(actionCloseLaborTicketModal());
      history.push({
        pathname: Routes.machineIdHomePath(machine.id),
      });
    },
  };
};

export const getCreateActivityOptions = ({
  dispatch,
  t,
  pendingActivityTypeName,
}) => {
  return {
    fetchPolicy: 'no-cache', // this ensures update will only be called once for mutation
    update: (
      _cache,
      {
        data: {
          createActivityResponse: { activity },
        },
      }
    ) => {
      dispatch(actionOptimisticActivityCreate({ activity }));
    },
    onCompleted: ({ createActivityResponse: { activity } }) => {
      throwSuccessToast(`${t('Entered:')} ${pendingActivityTypeName}`);
      dispatch(
        actionCanonicalActivityCreate({ activity, optimisticActivityRef })
      );
      dispatch(actionCloseLaborTicketModal());
    },
    onError: () => {
      dispatch(
        actionCanonicalActivityDelete({ activityRef: optimisticActivityRef })
      );
      throwErrorToast(t(`${t('Could not enter:')} ${pendingActivityTypeName}`));
      dispatch(actionCloseLaborTicketModal());
    },
  };
};

export const getRequestUpdateActivityOptions = ({
  dispatch,
  t,
  latestActivity,
  history,
  machine,
}) => {
  return {
    fetchPolicy: 'no-cache',
    update: (
      _cache,
      {
        data: {
          updateActivityResponse: { activity },
        },
      }
    ) => {
      dispatch(actionOptimisticActivityUpdate({ activity }));
    },
    onCompleted: ({ updateActivityResponse: { activity } }) => {
      throwSuccessToast(t('Successfully Paused'));
      dispatch(actionCanonicalActivityUpdate({ activity }));
      dispatch(actionCloseLaborTicketModal());
    },
    onError: () => {
      dispatch(
        actionCanonicalActivityUpdate({
          activity: {
            activityRef: latestActivity?.activityRef,
            end: null,
          },
        })
      );
      history.push(Routes.machineIdPath(machine.id));
      throwErrorToast(t('Failed to Pause'));
      dispatch(actionCloseLaborTicketModal());
    },
  };
};
